/**
 * Generic email and input validation utilities.
 * Pure standards-compliant logic without any hardcoded addresses or domains.
 */

// Standard RFC 5322 compliant email regex with valid multi-part domain and TLD check
export const EMAIL_REGEX =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

/**
 * Validates whether an email string is structurally valid.
 */
export function isValidEmail(email: string): boolean {
  if (!email || typeof email !== 'string') return false;
  const trimmed = email.trim();
  if (trimmed.length === 0 || trimmed.length > 254) return false;
  if (!EMAIL_REGEX.test(trimmed)) return false;

  const parts = trimmed.split('@');
  if (parts.length !== 2) return false;
  const domain = parts[1];
  const domainParts = domain.split('.');
  const tld = domainParts[domainParts.length - 1];
  // TLD must be at least 2 characters (e.g. .com, .in, .org)
  if (!tld || tld.length < 2) return false;

  return true;
}

/**
 * Validates an email and returns a user-friendly error message if invalid.
 */
export function validateEmail(email: string): { isValid: boolean; error: string | null } {
  const trimmed = (email ?? '').trim();
  if (!trimmed) {
    return { isValid: false, error: 'Email address is required.' };
  }
  if (!isValidEmail(trimmed)) {
    return {
      isValid: false,
      error: 'Please enter a valid email address (e.g. name@example.com).',
    };
  }
  return { isValid: true, error: null };
}
